<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { sanitizeText } from "@/common/utils/text";
import {
  baseInput,
  baseCheckbox,
  baseLoading,
  svgIcon,
} from "@/common/components";

const props = withDefaults(
  defineProps<{
    id: string;
    title: string;
    fieldId: string;
    fieldOption: string;
    list: any[];
    added: string[];
    open: boolean;
    width?: string;
    loading?: boolean;
    outsideClose?: boolean;
  }>(),
  {
    open: false,
    outsideClose: true,
    loading: false,
    width: "50rem",
  }
);

const { t } = useI18n();
const emit = defineEmits(["close", "add:item", "remove:item"]);

const search = ref("");
const internalOpen = ref(props.open);

const options = computed(() =>
  props.list
    ?.filter((item) => {
      if (search.value?.trim()) {
        const name = sanitizeText(
          (item[props.fieldOption] || "").toLowerCase()
        );
        const searching = sanitizeText((search.value || "").toLowerCase());

        if (name.includes(searching)) return item;
      } else {
        return item;
      }
    })
    ?.map((item) => ({
      id: item[props.fieldId],
      option: item[props.fieldOption],
      value: props.added.includes(item[props.fieldId]),
    }))
);

function checked(data: any) {
  const [option] = props.list?.filter(
    (item) => item[props.fieldId] === data.id
  );

  if (option) {
    if (data.value) {
      emit("add:item", option);
    } else {
      emit("remove:item", option);
    }
  }
}

function addHidePageScroll() {
  document.querySelector("html")?.classList.add("no-scroll");
}

function removeHidePageScroll() {
  document.querySelector("html")?.classList.remove("no-scroll");
}

function sidebarClose() {
  internalOpen.value = false;

  setTimeout(() => {
    emit("close");
  }, 350);
}

function sidebarOutsideClose() {
  if (props.outsideClose) sidebarClose();
}

onBeforeUnmount(() => {
  removeHidePageScroll();
});

onMounted(() => {
  addHidePageScroll();
});
</script>

<template>
  <Teleport to="body">
    <article class="sidebar-list-checkbox">
      <div class="outside" @click="sidebarOutsideClose()" />
      <section
        class="sidebar"
        :class="{
          'slide-left': props.open && internalOpen,
          'slide-right': !internalOpen,
        }"
      >
        <template v-if="props.loading">
          <div class="header">
            <div class="close">
              <button class="btn btn-default" @click="sidebarClose()">x</button>
            </div>
            <section class="header-title">
              {{ props.title }}
            </section>
          </div>
          <div class="content">
            <base-loading />
          </div>
        </template>
        <template v-else>
          <div class="header">
            <div class="close">
              <button class="btn btn-default" @click="sidebarClose()">x</button>
            </div>
            <section class="header-title">
              {{ props.title }}
            </section>

            <base-input
              v-model="search"
              background="#fff"
              color="#000"
              :placeholder="t('common.labels.search')"
            >
              <template #sufix>
                <div class="icon">
                  <svg-icon icon="search" size="1.75rem" />
                </div>
              </template>
            </base-input>

            <div class="header-subtitle">
              {{ t("common.texts.search_multiple") }}
            </div>
          </div>

          <div class="content">
            <base-checkbox
              :id="props.id"
              :options="options"
              :value="options"
              @checked="checked($event)"
            >
            </base-checkbox>
          </div>
        </template>
      </section>
    </article>
  </Teleport>
</template>

<style lang="scss" scoped>
$width: v-bind("props.width");

.sidebar-list-checkbox {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 98;
  display: grid;
  grid-template-columns: 1fr auto;
  width: 100vw;
  height: 100dvh;
  background: var(--black-transparent);

  .outside {
    flex: 1;
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: $width;
    height: 100dvh;
    background: #ffffff;
    padding: 1rem;

    .header {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .close {
        button {
          padding: 0;
          width: 1.5rem;
          height: 1.5rem;
        }
      }

      .header-title {
        display: flex;
        flex-direction: column;
        padding-block: 0.75rem;
        padding-inline: 1rem;
        font-size: 1.1rem;
        background: var(--white-off);
      }

      .header-subtitle {
        padding-inline: 0.5rem;
        font-size: 0.875rem;
      }
    }

    .content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: auto;
      padding-block-end: 1rem;
    }
  }
}

@-webkit-keyframes slide-left {
  0% {
    -webkit-transform: translate($width);
    transform: translateX($width);
  }
  100% {
    -webkit-transform: translate(0);
    transform: translateX(0);
  }
}
@keyframes slide-left {
  0% {
    transform: translate($width);
  }
  100% {
    transform: translateX(0);
  }
}

@-webkit-keyframes slide-right {
  0% {
    -webkit-transform: translate(0);
    transform: translateX(0);
  }
  100% {
    -webkit-transform: translate($width);
    transform: translateX($width);
  }
}
@keyframes slide-right {
  0% {
    transform: translate(0);
  }
  100% {
    transform: translateX($width);
  }
}

.slide-left {
  -webkit-animation: slide-left 300ms both;
  animation: slide-left 300ms both;
}

.slide-right {
  -webkit-animation: slide-right 300ms both;
  animation: slide-right 300ms both;
}
</style>
